/**
 * Defense in depth for audit §10 D2/D3: the legacy back-office round-tripped
 * the tenant's Stripe SECRET key (`meta.stripeKey`), the ForwardHub
 * service-account password and service-account passwords through the
 * browser. The port's backend no longer returns them — this module makes
 * sure the SPA never renders one even if a regressed backend did.
 *
 * Matches on the KEY NAME, never on values. Deliberately not a bare `key`
 * match: `subscriptionLimits[].key` ("bots") is ordinary catalogue data.
 */
export const SECRET_FIELD_PATTERN = /secret|passw|pwd|token|credential|stripekey|apikey|api_key|privatekey|private_key|signingkey/i

export function isSecretField(name: string): boolean {
  return SECRET_FIELD_PATTERN.test(name)
}

export const REDACTED = '[redacted]'

/** Deep copy with every secret-named field's value replaced by `[redacted]`. */
export function redactSecrets<T>(value: T): T {
  if (Array.isArray(value)) return value.map((v) => redactSecrets(v)) as unknown as T
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = isSecretField(k) ? REDACTED : redactSecrets(v)
    }
    return out as T
  }
  return value
}

/** Deep copy with every secret-named field REMOVED (for data that is kept
 * in component state, so a secret cannot even sit in the JS heap longer
 * than the parse). */
export function stripSecrets<T>(value: T): T {
  if (Array.isArray(value)) return value.map((v) => stripSecrets(v)) as unknown as T
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      if (!isSecretField(k)) out[k] = stripSecrets(v)
    }
    return out as T
  }
  return value
}
